import Phaser from 'phaser';
import { SCENE, GAME_WIDTH } from '../config.js';
import { gameState } from '../state/gameState.js';
import { currentPhase } from '../state/dayCycle.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { SceneTransition } from '../ui/SceneTransition.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite } from '../ui/NatalkaSprite.js';
import { BikeController } from '../ui/BikeController.js';
import { reportLocation } from '../ui/locationTracker.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachSceneEditMode } from '../ui/SceneEditMode.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { AltanaSeats } from '../ui/AltanaSeats.js';
import { PlacementHud } from '../ui/PlacementHud.js';
import { placementTexture } from '../ui/seating.js';
import { attachPlacementMenu } from '../ui/placementInteraction.js';
import { applyPlacementSize, applyPlacementFlip } from '../ui/placementSizing.js';
import { ySortDepth, UI_DEPTH } from '../ui/ySort.js';
import { showBackyardUpgradeModal } from '../ui/BackyardUpgradeModal.js';
import { attachYardLook } from './yardLook.js';
import { BACKYARD_WORLD_W, BACKYARD_GATE, gardenArrivalData, floorForLevel, BACKYARD_SPAWN, BACKYARD_EXIT_SPAWN_X, BACKYARD_BIKE_PARKED_DEFAULTS, backyardBlocked, BACKYARD_X_BOUNDS, carAnchorAt } from './backyardLayout.js';
import { createCarSprite, fitCarHitArea, setCarDepth } from '../ui/CarSprite.js';
import { NEAR, carDisplayHeight, OUTDOOR_CHARACTER_SCALE, BACKYARD_PERSPECTIVE } from '../ui/carCatalog.mjs';
import { centreForFeet, feetForCentre, fitCharacter } from '../ui/outdoorScale.mjs';
import { showGaragePanel } from '../ui/GaragePanel.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isInputLocked } from '../ui/inputLock.js';
import { showToast } from '../ui/Toast.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { preloader } from './PreloadScene.js';
import { pinToCamera } from '../ui/pin.mjs';
import { attachSideScroll } from '../ui/sideScrollCamera.js';
import { iconText } from '../ui/iconText.js';
import { upgradeButton } from '../ui/gameUi.js';

const CAR_ACTION = 'Auto {car}';
// The front portrait's visual ground line sits below its centre (the art keeps ~10 % of its
// height empty under the tyres); y-sorting on it is what lets you walk in front of the car.
const CAR_BASE_OFFSET = 60;

// Podwórko — the side yard right of the front yard. Unlocked for 500💰 (MainScene handles
// the unlock modal), upgraded in 5 levels from the table button here. Outdoor decorations
// are placeable; bikes ride in and park. Altana seating is attached by AltanaSeats (plan 3).
export class BackyardScene extends Phaser.Scene {
  constructor() { super(SCENE.BACKYARD); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    // Real-world scale like the street, with the Podwórko's own (deeper) perspective: the
    // lawn runs from the fence line down to the viewer. See outdoorScale.mjs.
    this.worldScale = OUTDOOR_CHARACTER_SCALE;
    this.perspective = BACKYARD_PERSPECTIVE;
    const spawnX = data?.spawnX ?? BACKYARD_SPAWN.x;
    const spawnY = data?.spawnY ?? centreForFeet(this, data?.spawnFeetY ?? BACKYARD_SPAWN.feetY);
    // 1.5 screens wide since 2026-10-22: the camera follows sideways (see attachSideScroll below). The picture of this
    // time of day and Podwórko level under the live sky, with the altana bench and yard objects copied from it to
    // stand in front (scenes/yardLook.js).
    this._yard = attachYardLook(this, { side: 'back', phase: currentPhase(), level: () => this._level() });

    this.natalka = new NatalkaSprite(this, spawnX, spawnY, { worldScale: this.worldScale });
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.natalka.setDepth(10);
    this.walker = new WalkController(this, this.natalka, {
      floorPolyline: floorForLevel(this._level()),
      // Slide around the barrel / logs / sapling (level read live: an upgrade swaps them).
      blocked: (fx, fy) => backyardBlocked(this._level())(fx, fy),
      xBounds: BACKYARD_X_BOUNDS,
      onBroadcast: (p) => realtime.move(p),
    });
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });

    // Bike — you can ride in from the front yard and park here.
    this.bike = new BikeController(this, {
      natalka: this.natalka,
      // The rider shares the walker's ground: the same floor line, the same obstacles to
      // slide around and the same side bounds — or the bike stopped where a walker could go.
      floor: {
        floorPolyline: floorForLevel(this._level()),
        blocked: (fx, fy) => backyardBlocked(this._level())(fx, fy),
        xBounds: BACKYARD_X_BOUNDS,
      },
      parkedDefaults: BACKYARD_BIKE_PARKED_DEFAULTS,
      onBroadcast: (p) => realtime.move(p),
    });
    this._scroll = attachSideScroll(this, { worldW: BACKYARD_WORLD_W, getTarget: () => (this.bike?.isRiding ? this.bike.rider : this.natalka) });

    this.transition = new SceneTransition(this);
    this.transition.addExitZone({ side: 'left', toScene: SCENE.MAIN, label: '{arrow_left} Przed dom', data: { spawnX: BACKYARD_EXIT_SPAWN_X } });
    // The gate in the back fence leads to the garden (spec 2026-10-22). On a bike it stays shut: exits are
    // only checked on foot, and the rider stops at the fence line anyway.
    this.transition.addExitZone({ side: 'rect', rect: BACKYARD_GATE.zone, labelAt: BACKYARD_GATE.label,
      toScene: SCENE.FARM, label: '{arrow_up} Ogród', data: gardenArrivalData() });

    new MoneyBar(this);
    attachFriendsButton(this);

    // Upgrade table button (same spot as the house's "🏠 Ulepsz" in MainScene).
    // HUD: above every world layer, including the sapling's foreground slice, whose canopy
    // reaches this corner (foreground layers sort at their base y, up to 720).
    this.upgradeBtn = upgradeButton(this, '{yard} Ulepsz podwórko', () => showBackyardUpgradeModal(this), { depth: UI_DEPTH });
    pinToCamera(this.upgradeBtn);   // stays on screen while the yard scrolls
    this._refreshUpgradeBtn();

    // Cars parked in the garage — drawn on the driveway in front of the door.
    this._carSprites = new Map();
    this._carTip = iconText(this, 0, 0, 'Twoje auto {car}', {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
      backgroundColor: '#000000aa', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(50).setVisible(false);
    this._renderCars();
    this._carSpaceHandler = () => this._onCarSpace();
    this.input.keyboard.on('keydown-SPACE', this._carSpaceHandler);

    // Decorations placed here (backend scene "backyard").
    this._decorSprites = new Map();
    this._renderDecorations();
    new PlacementHud(this);

    this._unsub = gameState.on(() => this._applyState());
    this.pollTimer = this.time.addEvent({ delay: 30000, loop: true, callback: () => gameState.refresh().catch(() => {}) });
    // `once`: Phaser reuses the scene instance across restarts, so `.on` would stack a new
    // listener on every entry. create() re-registers it, and the handler reads the CURRENT
    // this._unsub / this.pollTimer, so each entry still cleans up its own (the yard look cleans up after itself).
    this.events.once('shutdown', () => {
      this._unsub?.();
      this.pollTimer?.remove();
      this.input.keyboard.off('keydown-SPACE', this._carSpaceHandler);
      const a = virtualInput.getAction();
      if (a && a.label === CAR_ACTION) virtualInput.clearAction();
    });

    attachCoordsDebug(this);
    if (import.meta.env?.DEV) attachSceneEditMode(this);  // press E: author floor polylines / foreground polygons

    // Altana (lvl ≥ 3): seating (its bench occluder is part of the yard look's foreground).
    this.altanaSeats = new AltanaSeats(this, {
      natalka: this.natalka,
      walker: this.walker,
      getLevel: () => this._level(),
      getPeers: () => this._presence ? this._presence.avatars.values() : [],
      onBroadcast: (p) => realtime.move(p),
    });

    if (window.__myId) {
      this._presence = attachRemotePresence(this, `house:${window.__myId}:${this.scene.key}`, {
        seatAnchor: (seat) => this.altanaSeats.seatAnchorCentre(seat),
      });
    }
    if (window.__myId) attachRoomChat(this, `house:${window.__myId}:${this.scene.key}`);
  }

  update(_t, dt) {
    this._yard.update(currentPhase(), dt);
    if (this.bike.isRiding) {
      this.bike.update(dt);
      if (this.bike.rider.x <= 150) this.scene.start(SCENE.MAIN, { spawnX: BACKYARD_EXIT_SPAWN_X });
    } else {
      this.altanaSeats.update();
      this.walker.update(dt);
      if (!this.altanaSeats.isSeated) { this.transition.checkExits(this.natalka); fitCharacter(this); }
    }
    // While seated the action slot is AltanaSeats' ("Wstań"); letting BikeController also
    // register 'rower' every frame made the mobile action button flicker between the two,
    // and a tap could mount a nearby parked bike while she was still on the bench.
    if (!this.altanaSeats.isSeated) this.bike?.syncBikeAction();
    this._syncCarAction();
    const m = this.bike.isRiding ? this.bike.rider : this.natalka;
    reportLocation(this, m.x, m.y);
    this._depthSort();
    this._scroll.update(dt);
  }

  _level() { return gameState.raw?.backyard?.level ?? 1; }

  // --- cars ---------------------------------------------------------------------------

  _garageCars() {
    return (gameState.raw?.cars?.owned ?? []).filter((c) => c.location?.scene === 'garage');
  }

  _renderCars() {
    const wanted = new Map();
    this._garageCars().forEach((c, i) => {
      const anchor = carAnchorAt(this._level(), i);
      if (anchor) wanted.set(c.id, { car: c, anchor });
    });
    for (const [id, cont] of [...this._carSprites]) {
      if (!wanted.has(id) || cont.carModel !== wanted.get(id).car.model) {
        cont.destroy();
        this._carSprites.delete(id);
      }
    }
    for (const [id, { car, anchor }] of wanted) {
      let cont = this._carSprites.get(id);
      if (!cont) {
        cont = createCarSprite(this, car.model, 'front', anchor.x, anchor.y, { plate: car.plate });
        this._makeCarInteractive(cont, id);
        this._carSprites.set(id, cont);
      }
      cont.setPosition(anchor.x, anchor.y);
      setCarDepth(cont, ySortDepth(anchor.y + CAR_BASE_OFFSET));
    }
    this._carTip.setVisible(false);
  }

  // A Container has no texture to hit-test against, so give it the body's box.
  _makeCarInteractive(cont, carId) {
    const h = cont.img?.displayHeight ?? carDisplayHeight(cont.carModel, 'front');
    fitCarHitArea(cont, { useHandCursor: true });
    cont.on('pointerover', () => this._carTip.setPosition(cont.x, cont.y - h / 2 - 6).setVisible(true));
    cont.on('pointerout', () => this._carTip.setVisible(false));
    cont.on('pointerup', () => {
      if (!this._canUseCar()) return;   // riding / seated / a modal is up
      if (this._nearestCarId() === carId) this._openCar(carId);
      else showToast(this, 'Podejdź bliżej do auta {car}');
    });
  }

  _nearestCarId() {
    let best = null, bestD = Infinity;
    for (const [id, cont] of this._carSprites) {
      const d = Phaser.Math.Distance.Between(this.natalka.x, this.natalka.y, cont.x, cont.y + CAR_BASE_OFFSET);
      if (d <= NEAR && d < bestD) { best = id; bestD = d; }
    }
    return best;
  }

  _canUseCar() {
    return !isInputLocked(this) && !this.bike?.isRiding && !this.altanaSeats?.isSeated;
  }

  _openCar(carId) {
    const car = this._garageCars().find((c) => c.id === carId);
    if (car) showGaragePanel(this, car);
  }

  _onCarSpace() {
    if (!this._canUseCar()) return;
    const id = this._nearestCarId();
    if (id) this._openCar(id);
  }

  // Mobile action slot. Only ever claims the slot when it is free or already ours, so the
  // bike's "rower" (registered every frame while she stands next to one) wins instead of
  // the two of them fighting over the button.
  _syncCarAction() {
    const a = virtualInput.getAction();
    const mine = a && a.label === CAR_ACTION;
    const id = this._canUseCar() ? this._nearestCarId() : null;
    if (id && (mine || !a)) virtualInput.registerAction(CAR_ACTION, () => this._openCar(id));
    else if (!id && mine) virtualInput.clearAction();
  }

  _renderDecorations() {
    for (const sp of this._decorSprites.values()) sp.destroy();
    this._decorSprites.clear();
    const placements = (gameState.raw?.placements ?? []).filter((p) => p.scene === 'backyard');
    for (const p of placements) {
      const tex = placementTexture(p);
      const sprite = this.add.image(p.x, p.y, this.textures.exists(tex) ? tex : 'plot_empty')
        .setInteractive({ useHandCursor: true });
      if (!this.textures.exists(tex)) preloader.whenReady(tex, () => { if (sprite.active) { sprite.setTexture(tex); applyPlacementSize(sprite, p.item); } });
      applyPlacementSize(sprite, p.item);
      applyPlacementFlip(sprite, p);
      attachPlacementMenu(this, sprite, p);
      this._decorSprites.set(p.id, sprite);
    }
  }

  _depthSort() {
    this.natalka.setDepth(ySortDepth(feetForCentre(this, this.natalka.y)));
    // On the bike the rider sprite stands in for the character: sort it by its wheel
    // line like everything else, or it stays at the bike's parked depth (1) and every
    // lamp post and bush placed in the yard draws over it.
    if (this.bike?.isRiding) this.bike.rider.setDepth(ySortDepth(this.bike.rider.y + this.bike.riderWheelOffset));
    for (const sp of this._decorSprites.values()) sp.setDepth(ySortDepth(sp.y + sp.displayHeight / 2));
    if (this._presence) {
      for (const a of this._presence.avatars.values()) a.sprite.setDepth(ySortDepth(a.groundY ?? feetForCentre(this, a.sprite.y)));
    }
  }

  _applyState() {
    // (the picture and its foreground follow the level by themselves: scenes/yardLook.js)
    // Level changed → new floor line (the garage/altana footprint moves).
    this.walker.setFloorPolyline(floorForLevel(this._level()));
    this.bike?.riderWalker.setFloorPolyline(floorForLevel(this._level()));
    this.natalka?.setOutfit(gameState.raw?.inventory?.equipped);
    this.bike?.refresh();
    this._renderCars();
    this._renderDecorations();
    this._refreshUpgradeBtn();
  }

  _refreshUpgradeBtn() {
    const b = gameState.raw?.backyard;
    if (!b || !this.upgradeBtn) return;
    this.upgradeBtn.setLabel(b.is_max_level ? '{yard} Podwórko na max!' : `{yard} Ulepsz · ${b.next_upgrade_cost} {coin}`);
  }
}
